/**
 * Tinted Lens Simulator - Hub Page JS
 * Refactored to match Demo approach (simpler overlay on right side)
 */

class TintedLensSimulator {
  constructor() {
    this.state = {
      scene: null,
      lens: null,
      split: 0.5,
      isDragging: false,
    };
    // 默认 Hub 自动随场景切换镜片；若页面设置 window.SIMULATOR_AUTO_SELECT=false，则保持用户选定的镜片不变
    this.autoSelectOnSceneChange = window.SIMULATOR_AUTO_SELECT !== false;

    this.dom = {
      sceneItems: document.querySelectorAll('.scene-item'),
      lensRecList: document.querySelector('.lens-list-recommended'),
      lensOtherList: document.querySelector('.lens-list-other'),
      preview: document.querySelector('.simulator-preview'),
      sceneImage: document.getElementById('scene-image'),
      tintLayer: document.getElementById('tint-layer'),
      handle: document.getElementById('simulator-handle'),
      infoTitle: document.querySelector('.lens-info-title'),
      infoDesc: document.querySelector('.lens-info-desc'),
      ctaButton: document.querySelector('.lens-cta-button'),
      currentSceneLabel: document.querySelector('.current-scene-label'),
      currentLensLabel: document.querySelector('.current-lens-label'),
    };

    this.data = window.SIMULATOR_DATA || { lenses: {}, scenes: {} };

    // 图片预加载缓存（记录已预加载的图片 URL）
    this.preloadedImages = new Set();

    this.init();
  }

  init() {
    const params = new URLSearchParams(window.location.search);
    const sceneParam = params.get('scene');
    const lensParam = params.get('lens');
    const defaultLens = window.SIMULATOR_DEFAULT_LENS;

    this.sortSceneThumbnails();

    const sceneKeys = Object.keys(this.data.scenes || {});
    const lensKeys = Object.keys(this.data.lenses || {});
    let initialScene = this.data.scenes[sceneParam] ? sceneParam : null;
    if (!initialScene && defaultLens) {
      let bestHandle = null;
      let bestOrder = Infinity;
      for (let i = 0; i < sceneKeys.length; i += 1) {
        const key = sceneKeys[i];
        const scene = this.data.scenes[key];
        const rec = scene?.recommended_lenses || [];
        if (!rec.includes(defaultLens)) continue;
        const orderRaw = scene?.display_order;
        const orderNum = Number(orderRaw);
        const order = Number.isFinite(orderNum) ? orderNum : 9999;
        if (order < bestOrder) {
          bestOrder = order;
          bestHandle = key;
        }
      }
      if (bestHandle) initialScene = bestHandle;
    }
    if (!initialScene) initialScene = sceneKeys[0];

    let initialLens = this.data.lenses[lensParam] ? lensParam : null;
    if (!initialLens && defaultLens && this.data.lenses[defaultLens]) {
      initialLens = defaultLens;
    }
    if (!initialLens && initialScene) {
      const rec = this.data.scenes[initialScene]?.recommended_lenses || [];
      if (rec.length && this.data.lenses[rec[0]]) {
        initialLens = rec[0];
      }
    }
    if (!initialLens) initialLens = lensKeys[0];

    this.selectScene(initialScene, false);
    this.selectLens(initialLens, false);
    this.setSplit(this.state.split);
    this.bindSlider();
    this.bindSceneClicks();
    this.bindHistory();

    // 预加载场景图片（延迟执行，避免阻塞主要内容）
    setTimeout(() => {
      this.preloadAdjacentScenes(initialScene);
    }, 500);
  }

  sortSceneThumbnails() {
    const scenes = this.data.scenes || {};
    const lists = document.querySelectorAll('.scene-list');
    if (!lists.length) return;

    const toOrder = (val) => {
      if (val === null || val === undefined || val === '') return 9999;
      const num = Number(val);
      return Number.isFinite(num) ? num : 9999;
    };

    lists.forEach((list) => {
      const items = Array.from(list.querySelectorAll('.scene-item'));
      if (items.length < 2) return;
      const decorated = items.map((el, idx) => ({
        el,
        idx,
        handle: el.dataset.handle,
      }));
      decorated.sort((a, b) => {
        const orderA = toOrder(scenes[a.handle]?.display_order);
        const orderB = toOrder(scenes[b.handle]?.display_order);
        if (orderA !== orderB) return orderA - orderB;
        return a.idx - b.idx;
      });
      decorated.forEach(({ el }) => list.appendChild(el));
    });

    this.dom.sceneItems = document.querySelectorAll('.scene-item');
  }

  /**
   * 预加载相邻场景的图片
   * @param {string} currentHandle - 当前场景的 handle
   */
  preloadAdjacentScenes(currentHandle) {
    const sceneKeys = Object.keys(this.data.scenes || {});
    if (sceneKeys.length <= 1) return;

    const currentIndex = sceneKeys.indexOf(currentHandle);
    if (currentIndex === -1) return;

    // 预加载的场景列表：前一个和后一个
    const toPreload = [];

    // 前一个场景
    if (currentIndex > 0) {
      toPreload.push(sceneKeys[currentIndex - 1]);
    }

    // 后一个场景
    if (currentIndex < sceneKeys.length - 1) {
      toPreload.push(sceneKeys[currentIndex + 1]);
    }

    // 如果是第一个场景，额外预加载第二个和第三个
    if (currentIndex === 0 && sceneKeys.length > 2) {
      toPreload.push(sceneKeys[2]);
    }

    // 使用 Image 对象预加载（检查缓存避免重复）
    toPreload.forEach(handle => {
      const scene = this.data.scenes[handle];
      if (scene && scene.image && !this.preloadedImages.has(scene.image)) {
        const img = new Image();
        img.decoding = 'async';
        img.loading = 'lazy';
        img.src = scene.image;

        // 标记为已预加载
        this.preloadedImages.add(scene.image);
      }
    });
  }

  clamp01(n) {
    return Math.max(0, Math.min(1, n));
  }

  setSplit(ratio) {
    const r = this.clamp01(ratio ?? 0.5);
    this.state.split = r;
    if (this.dom.preview) {
      this.dom.preview.style.setProperty('--split', String(r));
    }
  }

  bindSlider() {
    const handle = this.dom.handle;
    const preview = this.dom.preview;
    if (!handle || !preview) return;

    const getRatioFromEvent = (e) => {
      const rect = preview.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      return (clientX - rect.left) / rect.width;
    };

    const onPointerDown = (e) => {
      this.state.isDragging = true;
      this.setSplit(getRatioFromEvent(e));
      window.addEventListener('mousemove', onPointerMove);
      window.addEventListener('mouseup', onPointerUp);
      window.addEventListener('touchmove', onPointerMove, { passive: false });
      window.addEventListener('touchend', onPointerUp);
    };

    const onPointerMove = (e) => {
      if (!this.state.isDragging) return;
      if (e.cancelable) e.preventDefault();

      if (this.state.rafId) cancelAnimationFrame(this.state.rafId);

      this.state.rafId = requestAnimationFrame(() => {
        this.setSplit(getRatioFromEvent(e));
        this.state.rafId = null;
      });
    };

    const onPointerUp = () => {
      this.state.isDragging = false;
      if (this.state.rafId) {
        cancelAnimationFrame(this.state.rafId);
        this.state.rafId = null;
      }
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
    };

    const onHandleKeyDown = (e) => {
      const current = this.state.split;
      const step = e.shiftKey ? 0.05 : 0.02;
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.setSplit(current - step); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.setSplit(current + step); }
      if (e.key === 'Home') { e.preventDefault(); this.setSplit(0); }
      if (e.key === 'End') { e.preventDefault(); this.setSplit(1); }
    };

    handle.addEventListener('mousedown', onPointerDown);
    handle.addEventListener('touchstart', onPointerDown, { passive: false });
    preview.addEventListener('mousedown', onPointerDown);
    preview.addEventListener('touchstart', onPointerDown, { passive: false });
    handle.addEventListener('keydown', onHandleKeyDown);
  }

  bindSceneClicks() {
    this.dom.sceneItems.forEach(item => {
      item.addEventListener('click', () => {
        const handle = item.dataset.handle;
        this.selectScene(handle, true);
      });
    });
  }

  bindHistory() {
    window.addEventListener('popstate', () => {
      const p = new URLSearchParams(window.location.search);
      this.selectScene(p.get('scene'), false);
      this.selectLens(p.get('lens'), false);
    });
  }

  renderLensButtons(container, handles) {
    if (!container) return;
    container.innerHTML = '';
    handles
      .map(h => this.data.lenses[h])
      .filter(Boolean)
      .forEach(lens => {
        const handle = lens.handle || lens.name;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lens-item';
        btn.dataset.handle = handle;
        btn.addEventListener('click', () => this.selectLens(btn.dataset.handle, true));

        const swatch = document.createElement('div');
        swatch.className = 'lens-swatch';
        if (lens.swatch_image) {
          const img = document.createElement('img');
          img.className = 'lens-swatch-image';
          img.src = lens.swatch_image;
          const altText = (lens.name || lens.short_label || '').toString().trim();
          img.alt = altText || 'Lens';
          img.loading = 'lazy';
          img.decoding = 'async';
          img.setAttribute('aria-hidden', 'true');
          swatch.appendChild(img);
        } else if (lens.tint_type === 'gradient') {
          const inner = document.createElement('div');
          inner.className = 'lens-swatch-inner';
          inner.style.background = `linear-gradient(to bottom, ${lens.color_primary || 'transparent'}, ${lens.color_secondary || 'transparent'})`;
          swatch.appendChild(inner);
        } else {
          swatch.style.backgroundColor = lens.color_primary || 'transparent';
        }

        const label = document.createElement('span');
        label.className = 'lens-name';
        label.textContent = lens.short_label || lens.name;

        btn.appendChild(swatch);
        btn.appendChild(label);
        container.appendChild(btn);
      });
  }

  renderLensLists(sceneHandle) {
    const scene = this.data.scenes[sceneHandle];
    if (!scene) return;
    const allHandles = Object.keys(this.data.lenses || {});
    const recommended = (scene.recommended_lenses || []).filter(h => this.data.lenses[h]);
    const recommendedSet = new Set(recommended);
    const others = allHandles.filter(h => !recommendedSet.has(h));

    // PDP 轻量版可能只有一个容器（无 lens-list-other）。此时在同一个列表中渲染全部镜片：
    // recommended 在前，others 在后（便于按场景推荐排序），但不影响用户自由选择。
    if (!this.dom.lensOtherList) {
      this.renderLensButtons(this.dom.lensRecList, [...recommended, ...others]);
    } else {
      this.renderLensButtons(this.dom.lensRecList, recommended);
      this.renderLensButtons(this.dom.lensOtherList, others);
    }
    this.updateLensActiveState();
  }

  updateLensActiveState() {
    const current = this.state.lens;

    // 强制清除所有镜片的 active 状态，防止初始化时的状态残留
    document.querySelectorAll('.lens-item.active').forEach(el => {
      el.classList.remove('active');
    });

    // 添加新元素的 active 类
    const currentEl = document.querySelector(`.lens-item[data-handle="${current}"]`);
    if (currentEl) {
      currentEl.classList.add('active');
      this.state.lastActiveBtn = current;
    }
  }

  selectScene(handle, updateUrl = true) {
    if (!handle || !this.data.scenes[handle]) return;

    this.state.scene = handle;
    const sceneData = this.data.scenes[handle];

    // Update UI active state
    this.dom.sceneItems.forEach(el => el.classList.toggle('active', el.dataset.handle === handle));
    if (this.dom.currentSceneLabel) {
      this.dom.currentSceneLabel.textContent = sceneData.name || '';
    }

    // Update scene image
    if (this.dom.sceneImage) {
      // 检查视口宽度 (<900px) 并优先使用移动端小图，以优化性能
      const isMobile = window.innerWidth < 900;
      const imageUrl = (isMobile && sceneData.image_mobile) ? sceneData.image_mobile : sceneData.image;

      this.dom.sceneImage.src = imageUrl;
      this.dom.sceneImage.alt = sceneData.alt_text || sceneData.name || 'Scene';
    }

    // 可选：切换场景时自动切换推荐镜片；PDP 可通过 window.SIMULATOR_AUTO_SELECT=false 禁用
    if (this.autoSelectOnSceneChange && sceneData.recommended_lenses && sceneData.recommended_lenses.length > 0) {
      const topPickLens = sceneData.recommended_lenses[0];
      if (this.data.lenses[topPickLens]) {
        this.selectLens(topPickLens, false);
      }
    }

    // Re-render lens lists (Only if hub containers exist)
    if (this.dom.lensRecList || this.dom.lensOtherList) {
      this.renderLensLists(handle);
    }

    if (updateUrl) this.updateUrl();

    // 预加载相邻场景（延迟执行，避免阻塞主线程）
    if (updateUrl) {  // 只在用户主动切换时预加载，避免初始化时重复
      setTimeout(() => {
        this.preloadAdjacentScenes(handle);
      }, 300);
    }
  }

  selectLens(handle, updateUrl = true) {
    if (!handle || !this.data.lenses[handle]) return;

    this.state.lens = handle;
    const lensData = this.data.lenses[handle];

    // Update active state
    this.updateLensActiveState();

    // Apply tint to overlay (Demo approach: simple background + opacity)
    this.applyTint(lensData);

    // Update info panel
    if (this.dom.infoTitle) this.dom.infoTitle.textContent = lensData.name;
    if (this.dom.infoDesc) this.dom.infoDesc.textContent = lensData.description || '';
    if (this.dom.currentLensLabel) this.dom.currentLensLabel.textContent = lensData.name;
    if (this.dom.ctaButton && lensData.url) {
      this.dom.ctaButton.href = lensData.url;
    }

    if (updateUrl) this.updateUrl();
  }

  /**
   * Apply tint using Demo approach:
   * - Set --tint-color as CSS background
   * - Set --tint-opacity for transparency
   * Works for both solid and gradient tints
   */
  applyTint(lens) {
    const layer = this.dom.tintLayer;
    if (!layer) return;

    const hexToRgb = (hex) => {
      const raw = String(hex || '').trim();
      if (!raw.startsWith('#')) return null;

      const short = raw.length === 4; // #RGB
      const full = raw.length === 7; // #RRGGBB
      const fullAlpha = raw.length === 9; // #RRGGBBAA
      if (!short && !full && !fullAlpha) return null;

      const expand = (s) => s.split('').map((c) => c + c).join('');
      const body = short ? expand(raw.slice(1)) : raw.slice(1, 7);
      const r = Number.parseInt(body.slice(0, 2), 16);
      const g = Number.parseInt(body.slice(2, 4), 16);
      const b = Number.parseInt(body.slice(4, 6), 16);
      if (![r, g, b].every(Number.isFinite)) return null;

      let a = null;
      if (fullAlpha) {
        const aa = Number.parseInt(raw.slice(7, 9), 16);
        if (Number.isFinite(aa)) a = aa / 255;
      }

      return { r, g, b, a };
    };

    const colorToRgba = (color, alpha) => {
      const raw = String(color || '').trim();
      const a = alpha == null ? null : this.clamp01(alpha);
      const rgb = hexToRgb(raw);
      if (rgb) {
        const finalAlpha = a == null ? (rgb.a == null ? 1 : rgb.a) : a;
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${finalAlpha})`;
      }
      if (a == null) return raw || 'transparent';
      return raw || 'transparent';
    };

    // Calculate base opacity (use opacity_percent if available, otherwise default)
    const opacityPercent = lens.opacity_percent == null ? 30 : Number(lens.opacity_percent);
    const baseOpacity = this.clamp01((Number.isFinite(opacityPercent) ? opacityPercent : 30) / 100);
    let layerOpacity = baseOpacity;

    // Determine color/gradient
    let tintColor = lens.color_primary || 'transparent';

    if (lens.tint_type === 'gradient') {
      // Handle gradient
      let gradientStops = null;
      if (lens.render_config) {
        try {
          const parsed = typeof lens.render_config === 'string' ? JSON.parse(lens.render_config) : lens.render_config;
          gradientStops = parsed?.gradient_stops;
        } catch (err) {
          // fallback
        }
      }

      if (Array.isArray(gradientStops) && gradientStops.length) {
        const allStrings = gradientStops.every((s) => typeof s === 'string');
        const allObjects = gradientStops.every((s) => s && typeof s === 'object');

        if (allStrings) {
          tintColor = `linear-gradient(to bottom, ${gradientStops.join(', ')})`;
        } else if (allObjects) {
          const stops = gradientStops
            .map((stop) => {
              const posRaw = stop.pos ?? stop.position ?? stop.pct ?? null;
              const posNum = typeof posRaw === 'string' ? Number(posRaw) : posRaw;
              if (!Number.isFinite(posNum)) return null;
              const posPct = posNum <= 1 && posNum >= 0 ? posNum * 100 : posNum;
              const pos = Math.max(0, Math.min(100, posPct));

              const color = stop.color ?? stop.value ?? stop.hex ?? null;
              if (typeof color !== 'string' || !color.trim()) return null;

              let alphaRaw = stop.alpha ?? stop.opacity ?? null;
              if (typeof alphaRaw === 'string' && alphaRaw.trim()) alphaRaw = Number(alphaRaw);
              let alpha = null;
              if (Number.isFinite(alphaRaw)) {
                alpha = alphaRaw > 1 ? alphaRaw / 100 : alphaRaw;
                alpha = this.clamp01(alpha);
              }

              return { pos, css: `${colorToRgba(color, alpha)} ${pos}%`, hasAlpha: alpha != null };
            })
            .filter(Boolean)
            .sort((a, b) => a.pos - b.pos);

          if (stops.length >= 2) {
            tintColor = `linear-gradient(to bottom, ${stops.map((s) => s.css).join(', ')})`;
            if (stops.some((s) => s.hasAlpha)) {
              layerOpacity = 1;
            }
          }
        }
      } else if (typeof gradientStops === 'string' && gradientStops.trim()) {
        tintColor = `linear-gradient(to bottom, ${gradientStops})`;
      } else {
        const color1 = lens.color_primary || 'transparent';
        const color2 = lens.color_secondary || 'transparent';
        tintColor = `linear-gradient(to bottom, ${color1}, ${color2})`;
      }
    }

    layer.style.setProperty('--tint-color', tintColor);
    layer.style.setProperty('--tint-opacity', String(layerOpacity));
    layer.style.setProperty('--tint-blend-mode', lens.blend_mode || 'multiply');
  }

  updateUrl() {
    const params = new URLSearchParams();
    if (this.state.scene) params.set('scene', this.state.scene);
    if (this.state.lens) params.set('lens', this.state.lens);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.simulatorInstance = new TintedLensSimulator();
});
